import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import type { Category } from "../../../hooks/useCategories";

interface CategoryManagerProps {
  categories: Category[];
  onRefresh: () => void;
}

const COLOR_OPTIONS = [
  { value: "bg-blue-600", label: "Azul", preview: "#2563eb" },
  { value: "bg-slate-700", label: "Gris oscuro", preview: "#334155" },
  { value: "bg-emerald-600", label: "Verde", preview: "#059669" },
  { value: "bg-orange-500", label: "Naranja", preview: "#f97316" },
  { value: "bg-rose-600", label: "Rosa", preview: "#e11d48" },
  { value: "bg-amber-600", label: "Ámbar", preview: "#d97706" },
  { value: "bg-red-500", label: "Rojo", preview: "#ef4444" },
  { value: "bg-blue-700", label: "Azul oscuro", preview: "#1d4ed8" },
  { value: "bg-teal-600", label: "Teal", preview: "#0d9488" },
  { value: "bg-indigo-600", label: "Índigo", preview: "#4f46e5" },
  { value: "bg-cyan-600", label: "Cian", preview: "#0891b2" },
  { value: "bg-lime-600", label: "Lima", preview: "#65a30d" },
  { value: "bg-pink-600", label: "Fucsia", preview: "#db2777" },
  { value: "bg-violet-600", label: "Violeta", preview: "#7c3aed" },
  { value: "bg-yellow-600", label: "Amarillo", preview: "#ca8a04" },
];

export default function CategoryManager({ categories, onRefresh }: CategoryManagerProps) {
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("bg-blue-600");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [checkingArticles, setCheckingArticles] = useState<number | null>(null);
  const [articleCount, setArticleCount] = useState<Record<number, number>>({});
  const [turismoCounts, setTurismoCounts] = useState<Record<string, number>>({});
  const [loadingTurismoCounts, setLoadingTurismoCounts] = useState(false);

  const turismoSubs = [
    { name: "Destinos", color: "bg-teal-600", icon: "ri-map-pin-line" },
    { name: "Hoteles", color: "bg-amber-600", icon: "ri-hotel-line" },
    { name: "Inversión", color: "bg-emerald-600", icon: "ri-funds-line" },
    { name: "Asociaciones", color: "bg-rose-600", icon: "ri-team-line" },
  ];

  useEffect(() => {
    const fetchTurismoCounts = async () => {
      setLoadingTurismoCounts(true);
      const counts: Record<string, number> = {};
      for (const sub of turismoSubs) {
        const { data, error } = await supabase
          .from("articles")
          .select("id", { count: "exact" })
          .eq("category", "Turismo")
          .eq("subcategory", sub.name);
        if (!error) {
          counts[sub.name] = data?.length || 0;
        }
      }
      setTurismoCounts(counts);
      setLoadingTurismoCounts(false);
    };
    fetchTurismoCounts();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) {
      setMessage("Error: El nombre es obligatorio.");
      return;
    }
    const exists = categories.some(
      (c) => c.name.toLowerCase() === newName.trim().toLowerCase()
    );
    if (exists) {
      setMessage("Error: Ya existe una categoría con ese nombre.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const maxOrder = categories.length > 0
        ? Math.max(...categories.map((c) => c.sort_order))
        : 0;
      const { error } = await supabase
        .from("categories")
        .insert([{ name: newName.trim(), color: newColor, sort_order: maxOrder + 1, active: true }]);
      if (error) throw error;
      setNewName("");
      setNewColor("bg-blue-600");
      setMessage("Categoría creada correctamente.");
      onRefresh();
    } catch (err) {
      setMessage("Error: " + (err instanceof Error ? err.message : "No se pudo crear"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    setCheckingArticles(id);
    setMessage("");
    try {
      const { data, error: countError } = await supabase
        .from("articles")
        .select("id")
        .or(`category.eq."${name}",tags.cs.{"${name}"}`);

      if (countError) throw countError;
      const count = data?.length || 0;
      setArticleCount((prev) => ({ ...prev, [id]: count }));

      if (count > 0) {
        setMessage(
          `Atención: "${name}" tiene ${count} artículo(s). Cambia la categoría de esas noticias antes de eliminarla.`
        );
        setCheckingArticles(null);
        return;
      }

      const confirmed = window.confirm(`¿Eliminar la categoría "${name}"? Esta acción no se puede deshacer.`);
      if (!confirmed) {
        setCheckingArticles(null);
        return;
      }

      const { error } = await supabase.from("categories").delete().eq("id", id);
      if (error) throw error;
      setMessage("Categoría eliminada.");
      onRefresh();
    } catch (err) {
      setMessage("Error: " + (err instanceof Error ? err.message : "No se pudo eliminar"));
    } finally {
      setCheckingArticles(null);
    }
  };

  const handleEditStart = (cat: Category) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditColor(cat.color);
  };

  const handleEditSave = async (id: number) => {
    if (!editName.trim()) {
      setMessage("Error: El nombre no puede estar vacío.");
      return;
    }
    const exists = categories.some(
      (c) => c.id !== id && c.name.toLowerCase() === editName.trim().toLowerCase()
    );
    if (exists) {
      setMessage("Error: Ya existe otra categoría con ese nombre.");
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from("categories")
        .update({ name: editName.trim(), color: editColor })
        .eq("id", id);
      if (error) throw error;
      setEditingId(null);
      setMessage("Categoría actualizada.");
      onRefresh();
    } catch (err) {
      setMessage("Error: " + (err instanceof Error ? err.message : "No se pudo actualizar"));
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (id: number, direction: "up" | "down") => {
    const idx = categories.findIndex((c) => c.id === id);
    if (idx === -1) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= categories.length) return;

    const reordered = [...categories];
    const temp = reordered[idx];
    reordered[idx] = reordered[targetIdx];
    reordered[targetIdx] = temp;

    const updates = reordered.map((c, i) =>
      supabase.from("categories").update({ sort_order: i + 1 }).eq("id", c.id)
    );
    await Promise.all(updates);
    onRefresh();
  };

  return (
    <div className="space-y-6">
      {/* Nueva categoría */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="bg-[#0B1F3A] px-6 py-4">
          <h2 className="text-white font-bold text-sm" style={{ fontFamily: "'Sora', sans-serif" }}>
            <i className="ri-add-circle-line mr-1.5" />
            Nueva categoría
          </h2>
        </div>
        <form onSubmit={handleCreate} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
                Nombre *
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ej: Seguridad Aérea"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
                maxLength={60}
              />
            </div>
            <div>
              <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
                Color del badge
              </label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setNewColor(opt.value)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer border-2 ${
                      newColor === opt.value ? "border-[#0B1F3A] scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: opt.preview }}
                    title={opt.label}
                  >
                    {newColor === opt.value && (
                      <i className="ri-check-line text-white text-xs" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {message && (
            <div
              className={`flex items-center gap-2 rounded-xl px-4 py-3 ${
                message.includes("Error") || message.includes("Atención")
                  ? "bg-amber-50 border border-amber-200 text-amber-700"
                  : "bg-emerald-50 border border-emerald-200 text-emerald-600"
              }`}
            >
              <i className={message.includes("Error") || message.includes("Atención") ? "ri-error-warning-line" : "ri-check-line"} />
              <p className="text-sm font-semibold">{message}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-[#0B1F3A] hover:bg-slate-800 text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-add-line" />}
            Agregar categoría
          </button>
        </form>
      </div>

      {/* Subcategorías de Turismo */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-teal-50/50 to-white">
          <h2 className="text-[#0B1F3A] font-bold text-sm flex items-center gap-2" style={{ fontFamily: "'Sora', sans-serif" }}>
            <i className="ri-map-2-line text-teal-600" />
            Subcategorías de Turismo
          </h2>
          <p className="text-slate-400 text-xs mt-1">
            Estas subcategorías aparecen en el filtro de la página de Turismo cuando se selecciona la categoría "Turismo" en una noticia.
          </p>
        </div>
        <div className="divide-y divide-slate-50">
          {turismoSubs.map((sub, idx) => (
            <div
              key={sub.name}
              className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
            >
              <div className="text-slate-300 text-xs font-bold w-6 text-center">
                {idx + 1}
              </div>
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${sub.color} flex-shrink-0`}>
                <i className={`${sub.icon} text-white text-sm`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[#0B1F3A] font-semibold text-sm">{sub.name}</span>
                  <span className={`inline-block text-white text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full ${sub.color}`}>
                    {sub.name}
                  </span>
                </div>
                <p className="text-slate-400 text-[10px] mt-0.5">
                  Subcategoría de Turismo
                </p>
              </div>
              <div className="text-slate-400 text-xs hidden sm:block">
                {loadingTurismoCounts ? (
                  <i className="ri-loader-4-line animate-spin text-slate-300" />
                ) : (
                  <span>{turismoCounts[sub.name] || 0} noticias</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
                  Fijo
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lista de categorías */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-[#0B1F3A] font-bold text-sm" style={{ fontFamily: "'Sora', sans-serif" }}>
            Categorías activas ({categories.length})
          </h2>
          <p className="text-slate-400 text-xs mt-1">
            Arrastra o usa las flechas para reordenar. El orden afecta cómo se muestran en filtros y secciones.
          </p>
        </div>

        <div className="divide-y divide-slate-50">
          {categories.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-slate-400 text-sm">No hay categorías configuradas.</p>
            </div>
          ) : (
            categories.map((cat, idx) => (
              <div
                key={cat.id}
                className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors group"
              >
                <div className="text-slate-300 text-xs font-bold w-6 text-center">
                  {idx + 1}
                </div>

                <div className={`w-4 h-4 rounded-full ${cat.color} flex-shrink-0`} />

                <div className="flex-1 min-w-0">
                  {editingId === cat.id ? (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 w-full sm:w-64"
                        maxLength={60}
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {COLOR_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setEditColor(opt.value)}
                            className={`w-6 h-6 rounded-md transition-all cursor-pointer border-2 ${
                              editColor === opt.value ? "border-[#0B1F3A]" : "border-transparent"
                            }`}
                            style={{ backgroundColor: opt.preview }}
                            title={opt.label}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-[#0B1F3A] font-semibold text-sm">{cat.name}</span>
                      <span className={`inline-block text-white text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full ${cat.color}`}>
                        {cat.name}
                      </span>
                    </div>
                  )}
                </div>

                <div className="text-slate-400 text-xs hidden sm:block">
                  {articleCount[cat.id] !== undefined
                    ? `${articleCount[cat.id]} noticias`
                    : ""}
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {editingId === cat.id ? (
                    <>
                      <button
                        onClick={() => handleEditSave(cat.id)}
                        disabled={saving}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors cursor-pointer"
                        title="Guardar"
                      >
                        <i className="ri-check-line text-sm" />
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer"
                        title="Cancelar"
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleMove(cat.id, "up")}
                        disabled={idx === 0}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors cursor-pointer disabled:opacity-30"
                        title="Subir"
                      >
                        <i className="ri-arrow-up-line text-sm" />
                      </button>
                      <button
                        onClick={() => handleMove(cat.id, "down")}
                        disabled={idx === categories.length - 1}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors cursor-pointer disabled:opacity-30"
                        title="Bajar"
                      >
                        <i className="ri-arrow-down-line text-sm" />
                      </button>
                      <button
                        onClick={() => handleEditStart(cat)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-pencil-line text-sm" />
                      </button>
                      <button
                        onClick={() => handleDelete(cat.id, cat.name)}
                        disabled={checkingArticles === cat.id}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer disabled:opacity-50"
                        title="Eliminar"
                      >
                        {checkingArticles === cat.id ? (
                          <i className="ri-loader-4-line animate-spin text-xs" />
                        ) : (
                          <i className="ri-delete-bin-line text-sm" />
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}